import { Link } from "react-router-dom";
import { Bot, ShieldCheck, LineChart, Users, Gift, ArrowRight, Wallet } from "lucide-react";
import { useFetch } from "@/components/common";
import { Logo } from "@/components/Shell";
import { usd } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const FEATURES = [
  [Bot, "AI trading plans", "Choose a plan, activate it from your balance and track daily returns on your dashboard."],
  [ShieldCheck, "Manually verified payments", "Every deposit and withdrawal is reviewed by our team and tracked by transaction ID."],
  [LineChart, "Transparent ledger", "Every credit and debit is recorded with its status, amount and timestamp."],
  [Users, "Referral & Partner programs", "Invite others with your code. Content creators can join the YouTuber Partner program."],
];

export default function Landing() {
  const { data: s } = useFetch("/public/settings");
  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <header className="sticky top-0 z-30 border-b border-[#1E2738] bg-[#0B0E14]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <Logo />
          <div className="flex items-center gap-2">
            <Link to="/login" className="nx-btn-ghost" data-testid="landing-login-link">Sign in</Link>
            <Link to="/register" className="nx-btn-primary" data-testid="landing-register-link">Open account</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/90 to-[#0B0E14]/40" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 sm:py-32">
          <div className="max-w-2xl">
            {s?.welcome_bonus_enabled && s.welcome_bonus_amount > 0 && (
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-1 text-xs text-[#10B981]" data-testid="landing-bonus-pill">
                <Gift size={13} /> {usd(s.welcome_bonus_amount)} welcome bonus on sign-up · terms apply
              </div>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Algorithmic trading,<br /><span className="text-[#10B981]">managed for you.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-400 leading-relaxed">
              AI Nexora gives you access to AI-driven trading plans with a clean dashboard for your balance,
              deposits, daily returns and withdrawals — every transaction verified and recorded.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" className="nx-btn-primary px-6 py-3" data-testid="hero-get-started">Get started <ArrowRight size={16} /></Link>
              <Link to="/login" className="nx-btn-secondary px-6 py-3" data-testid="hero-sign-in">I have an account</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[#1E2738] nx-grid-bg">
        <div className="mx-auto grid max-w-7xl gap-4 px-5 py-20 md:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map(([Icon, t, d]) => (
            <div key={t} className="nx-card p-6 transition-colors hover:border-[#10B981]/40">
              <span className="inline-grid h-10 w-10 place-items-center rounded-lg bg-[#10B981]/10 text-[#10B981]"><Icon size={19} /></span>
              <h3 className="mt-5 text-base font-semibold">{t}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16">
        <div className="nx-card flex flex-col items-start gap-6 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Wallet className="text-[#10B981]" />
            <div>
              <h3 className="text-lg font-semibold">Deposit via Bank, UPI or USDT</h3>
              <p className="text-sm text-slate-400">Pick a tier, pay your unique amount and submit the transaction ID.</p>
            </div>
          </div>
          <Link to="/register" className="nx-btn-primary" data-testid="cta-register">Create free account</Link>
        </div>
      </section>

      <footer className="border-t border-[#1E2738]">
        <div className="mx-auto max-w-7xl px-5 py-8 text-xs text-slate-500 leading-relaxed">
          <p>Trading involves risk. Returns are not guaranteed and past performance does not indicate future results. Promotional credits are subject to platform terms.</p>
          <p className="mt-2">© {new Date().getFullYear()} AI Nexora. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
